import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/firebase";
import "./css/ResourceSubmissionForm.css";

const ResourceSubmissionForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    description: "",
    website: "",
    field: [],
    year: [],
    race: "",
    "application-period": "",
    length: "",
    image: "",
    remote: false,
    paid: false,
    featured: false,
    tags: "",
    submitterName: "",
    submitterEmail: "",
    additionalNotes: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});

  // Form options
  const TYPE_OPTIONS = [
    { value: "internship", label: "Internship" },
    { value: "apprenticeship", label: "Apprenticeship" },
    { value: "program", label: "Program" },
    { value: "fellowship", label: "Fellowship" },
    {
      value: "professional-development-program",
      label: "Professional Development Program",
    },
    { value: "professional-organization", label: "Professional Organization" },
    { value: "graduate-level", label: "Graduate Level Program" },
    { value: "government-program", label: "Government Program" },
    { value: "scholarship", label: "Scholarship" },
    { value: "course", label: "Course" },
    { value: "event", label: "Event" },
    { value: "research", label: "Research Opportunity" },
    { value: "volunteer", label: "Volunteer Opportunity" },
  ];

  const FIELD_OPTIONS = [
    { value: "stem", label: "STEM" },
    { value: "health", label: "Health" },
    { value: "business & finance", label: "Business & Finance" },
    { value: "education", label: "Education" },
    { value: "arts & humanities", label: "Arts & Humanities" },
    { value: "social sciences", label: "Social Sciences" },
    { value: "open-to-all", label: "Open to All Fields" },
  ];

  const YEAR_OPTIONS = [
    { value: "open-to-all", label: "Open to All" },
    { value: "highschool", label: "High School" },
    { value: "community-college", label: "Community College" },
    { value: "freshmen", label: "Freshmen" },
    { value: "sophomore", label: "Sophomore" },
    { value: "junior", label: "Junior" },
    { value: "senior", label: "Senior" },
    { value: "graduate", label: "Graduate" },
    { value: "postdoctoral", label: "Postdoctoral" },
  ];

  const RACE_OPTIONS = [
    { value: "open-to-all", label: "Open to All" },
    { value: "latino/hispanic", label: "Latino/Hispanic" },
    { value: "black/african-american", label: "Black/African American" },
    { value: "asian/pacific-islander", label: "Asian/Pacific Islander" },
    {
      value: "native-american/indigenous",
      label: "Native American/Indigenous",
    },
  ];

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle multi-select changes
  const handleMultiSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: prev[name].includes(value)
        ? prev[name].filter((item) => item !== value)
        : [...prev[name], value],
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.type) newErrors.type = "Type is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";
    if (!formData.website.trim()) newErrors.website = "Website is required";

    // Email validation (only if provided)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.submitterEmail && !emailRegex.test(formData.submitterEmail)) {
      newErrors.submitterEmail = "Please enter a valid email address";
    }

    // URL validation
    const urlRegex = /^https?:\/\/.+/;
    if (formData.website && !urlRegex.test(formData.website)) {
      newErrors.website =
        "Please enter a valid URL (starting with http:// or https://)";
    }

    // Multi-select validation
    if (formData.field.length === 0)
      newErrors.field = "Please select at least one field";
    if (formData.year.length === 0)
      newErrors.year = "Please select at least one academic year";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitStatus({
        type: "error",
        message: "Please fix the errors above and try again.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare data for submission
      const submissionData = {
        ...formData,
        tags: formData.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag),
        status: "pending-review", // Mark for review
        submittedAt: serverTimestamp(),
        reviewedAt: null,
        reviewedBy: null,
        published: false,
      };

      // Submit to pending-submissions collection for review
      const docRef = await addDoc(
        collection(db, "pending-submissions"),
        submissionData
      );

      setSubmitStatus({
        type: "success",
        message:
          "Thank you! Your submission has been received and will be reviewed before publishing.",
        submissionId: docRef.id,
      });

      // Reset form
      setFormData({
        name: "",
        type: "",
        description: "",
        website: "",
        field: [],
        year: [],
        race: "",
        "application-period": "",
        length: "",
        image: "",
        remote: false,
        paid: false,
        featured: false,
        tags: "",
        submitterName: "",
        submitterEmail: "",
        additionalNotes: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus({
        type: "error",
        message: "There was an error submitting your form. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Multi-select component
  const MultiSelectField = ({
    name,
    options,
    selectedValues,
    label,
    error,
  }) => (
    <div className="form-group">
      <label className="form-label">
        {label} <span className="required">*</span>
      </label>
      <div className="multi-select-grid">
        {options.map((option) => (
          <label key={option.value} className="checkbox-option">
            <input
              type="checkbox"
              checked={selectedValues.includes(option.value)}
              onChange={() => handleMultiSelectChange(name, option.value)}
              className="checkbox-input"
            />
            <span className="checkbox-custom"></span>
            <span className="checkbox-label">{option.label}</span>
          </label>
        ))}
      </div>
      {error && <span className="error-message">{error}</span>}
    </div>
  );

  return (
    <div className="resource-submission-form">
      <div className="form-header">
        <h2>Submit a New Opportunity</h2>
        <p>
          Help expand our database by submitting career opportunities, programs,
          or resources for students.
        </p>
      </div>

      {submitStatus && (
        <div className={`status-message ${submitStatus.type}`}>
          <div className="status-icon">
            {submitStatus.type === "success" ? "✅" : "❌"}
          </div>
          <div className="status-content">
            <p>{submitStatus.message}</p>
            {submitStatus.submissionId && (
              <p className="submission-id">
                Submission ID: {submitStatus.submissionId}
              </p>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="submission-form">
        {/* Basic Information */}
        <section className="form-section">
          <h3>Basic Information</h3>

          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Opportunity Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`form-input ${errors.name ? "error" : ""}`}
              placeholder="e.g., Google STEP Internship"
            />
            {errors.name && (
              <span className="error-message">{errors.name}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="type" className="form-label">
              Type <span className="required">*</span>
            </label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className={`form-select ${errors.type ? "error" : ""}`}
            >
              <option value="">Select a type</option>
              {TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.type && (
              <span className="error-message">{errors.type}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description <span className="required">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className={`form-textarea ${errors.description ? "error" : ""}`}
              placeholder="Provide a detailed description of the opportunity..."
              rows="4"
            />
            {errors.description && (
              <span className="error-message">{errors.description}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="website" className="form-label">
              Website/Application Link <span className="required">*</span>
            </label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              className={`form-input ${errors.website ? "error" : ""}`}
              placeholder="https://example.com/apply"
            />
            {errors.website && (
              <span className="error-message">{errors.website}</span>
            )}
          </div>
        </section>

        {/* Eligibility & Requirements */}
        <section className="form-section">
          <h3>Eligibility & Requirements</h3>

          <MultiSelectField
            name="field"
            options={FIELD_OPTIONS}
            selectedValues={formData.field}
            label="Field/Major"
            error={errors.field}
          />

          <MultiSelectField
            name="year"
            options={YEAR_OPTIONS}
            selectedValues={formData.year}
            label="Academic Year"
            error={errors.year}
          />

          <div className="form-group">
            <label htmlFor="race" className="form-label">
              Diversity & Inclusion Focus
            </label>
            <select
              id="race"
              name="race"
              value={formData.race}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">Select if applicable</option>
              {RACE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </section>

        {/* Program Details */}
        <section className="form-section">
          <h3>Program Details</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="application-period" className="form-label">
                Application Deadline
              </label>
              <input
                type="text"
                id="application-period"
                name="application-period"
                value={formData["application-period"]}
                onChange={handleInputChange}
                className="form-input"
                placeholder="e.g., March 15, 2024 or Rolling"
              />
            </div>

            <div className="form-group">
              <label htmlFor="length" className="form-label">
                Program Length
              </label>
              <input
                type="text"
                id="length"
                name="length"
                value={formData.length}
                onChange={handleInputChange}
                className="form-input"
                placeholder="e.g., 12 weeks, 6 months"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="image" className="form-label">
              Company/Organization Logo
            </label>
            <input
              type="text"
              id="image"
              name="image"
              value={formData.image}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Logo filename (e.g., google.png) - optional"
            />
            <small className="form-hint">
              If you have a logo file, please mention it. We'll contact you for
              the file.
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Program Features</label>
            <div className="checkbox-group">
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  name="remote"
                  checked={formData.remote}
                  onChange={handleInputChange}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">
                  Remote/Virtual Option Available
                </span>
              </label>

              <label className="checkbox-option">
                <input
                  type="checkbox"
                  name="paid"
                  checked={formData.paid}
                  onChange={handleInputChange}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Paid Opportunity</span>
              </label>

              <label className="checkbox-option">
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleInputChange}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">
                  Highly Recommended (Featured)
                </span>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="tags" className="form-label">
              Tags
            </label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              className="form-input"
              placeholder="e.g., coding, research, leadership, mentorship (comma-separated)"
            />
            <small className="form-hint">
              Add relevant tags separated by commas to help with search and
              categorization.
            </small>
          </div>
        </section>

        {/* Submitter Information */}
        <section className="form-section">
          <h3>Your Information</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="submitterName" className="form-label">
                Your Name
              </label>
              <input
                type="text"
                id="submitterName"
                name="submitterName"
                value={formData.submitterName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Optional - for follow-up questions"
              />
            </div>

            <div className="form-group">
              <label htmlFor="submitterEmail" className="form-label">
                Your Email
              </label>
              <input
                type="email"
                id="submitterEmail"
                name="submitterEmail"
                value={formData.submitterEmail}
                onChange={handleInputChange}
                className={`form-input ${errors.submitterEmail ? "error" : ""}`}
                placeholder="your.email@example.com (optional)"
              />
              {errors.submitterEmail && (
                <span className="error-message">{errors.submitterEmail}</span>
              )}
              <small className="form-hint">
                Optional - We'll only use this to contact you if we need
                clarification about your submission.
              </small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="additionalNotes" className="form-label">
              Additional Notes
            </label>
            <textarea
              id="additionalNotes"
              name="additionalNotes"
              value={formData.additionalNotes}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Any additional information or special requirements..."
              rows="3"
            />
          </div>
        </section>

        {/* Submit Button */}
        <div className="form-actions">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`submit-btn ${isSubmitting ? "submitting" : ""}`}
          >
            {isSubmitting ? (
              <>
                <span className="loading-spinner"></span>
                Submitting...
              </>
            ) : (
              "Submit for Review"
            )}
          </button>
        </div>

        <div className="form-footer">
          <p>
            <strong>Review Process:</strong> All submissions are reviewed by our
            team before being published. You'll receive an email confirmation
            once your submission is approved.
          </p>
        </div>
      </form>
    </div>
  );
};

export default ResourceSubmissionForm;
